import React, { useState, useEffect } from 'react';
import { Mail, X, Phone, Calendar, MessageCircle } from 'lucide-react';
import PropertyLeadForm from './PropertyLeadForm';
import companyConfig from '../config/company';

/**
 * Multi-surface lead-conversion UI for the property detail page.
 *
 * Why this exists:
 *   - Production analytics showed 200+ visitors / 0 leads over 5 days.
 *   - Root cause: on mobile the existing PropertyLeadForm sat ~3600px down the
 *     page (4+ viewport heights). Visitors bounced before discovering it.
 *
 * What it ships:
 *   1. <InlineCTA />     — a single bold card placed mid-content (above the
 *      long description) so the user sees a call-to-action after only ~2 scrolls.
 *   2. <StickyCTA />     — a fixed bottom bar on mobile (always visible while
 *      scrolling). Used by every major real-estate portal (Idealista, Funda,
 *      Kyero). Both CTAs share the same modal so the form code stays single-
 *      source-of-truth.
 *   3. <LeadModal />     — overlay-style wrapper around the existing
 *      PropertyLeadForm. Closes on ESC, backdrop click, or successful submit.
 *
 * GA4: a `lead_cta_open` event is pushed to dataLayer when the modal opens,
 * tagged with the source (inline | sticky) so we can A/B which surface
 * converts better.
 */

const fireGAEvent = (source) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({ event: 'lead_cta_open', cta_source: source });
  }
};

export const InlineCTA = ({ property, onOpen }) => {
  return (
    <div style={styles.inlineCard} data-testid="property-inline-cta">
      <div style={styles.inlineLeft}>
        <h3 style={styles.inlineTitle}>
          Zainteresowany tą nieruchomością?
        </h3>
        <p style={styles.inlineText}>
          Poproś o więcej informacji lub umów oglądanie — bez zobowiązań.
        </p>
        <p style={styles.inlineIntlNote}>
          <span style={styles.inlineIntlTag}>ES</span>
          Międzynarodowy zespół w Hiszpanii — odpowiadamy po angielsku · <span style={styles.inlineIntlEn}>we reply in English</span>
        </p>
      </div>
      <div style={styles.inlineActions}>
        <button
          type="button"
          onClick={() => { fireGAEvent('inline'); onOpen('info'); }}
          style={styles.inlineBtnPrimary}
          data-testid="inline-cta-info"
        >
          <Mail size={16} />
          Poproś o informacje
        </button>
        <button
          type="button"
          onClick={() => { fireGAEvent('inline'); onOpen('viewing'); }}
          style={styles.inlineBtnSecondary}
          data-testid="inline-cta-viewing"
        >
          <Calendar size={16} />
          Umów oglądanie
        </button>
      </div>
    </div>
  );
};

export const StickyCTA = ({ property, onOpen }) => {
  // Hide the sticky bar while the cookie banner is up (it would be covered
  // anyway because the banner sits at z-index 1000). We re-check after a
  // micro-delay because the banner can mount slightly after the first paint.
  const [cookieAccepted, setCookieAccepted] = useState(() => {
    if (typeof window === 'undefined') return true;
    return !!localStorage.getItem('casaothers_cookie_consent');
  });
  useEffect(() => {
    if (cookieAccepted) return undefined;
    const tick = () => {
      if (localStorage.getItem('casaothers_cookie_consent')) setCookieAccepted(true);
    };
    const id = setInterval(tick, 600);
    return () => clearInterval(id);
  }, [cookieAccepted]);

  if (!cookieAccepted) return null;

  // Message is addressed to Pascal (our local expert) and must stay in Dutch.
  const waText = encodeURIComponent(
    `Hallo, ik heb interesse in ${property.title} (Ref. ${property.ref}). Kunt u mij meer informatie geven?`
  );
  const waHref = `${companyConfig.whatsappUrl}?text=${waText}`;

  return (
    <div style={styles.stickyBar} data-testid="property-sticky-cta">
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        style={styles.stickyWhatsapp}
        data-testid="sticky-cta-whatsapp"
        onClick={() => fireGAEvent('sticky_whatsapp')}
      >
        <MessageCircle size={18} />
        WhatsApp
      </a>
      <button
        type="button"
        onClick={() => { fireGAEvent('sticky'); onOpen('info'); }}
        style={styles.stickyPrimary}
        data-testid="sticky-cta-info"
      >
        <Mail size={18} />
        Poproś o informacje
      </button>
    </div>
  );
};

export const LeadModal = ({ open, onClose, property, defaultCta }) => {
  const submittedRef = React.useRef(false);

  // Reset the submitted flag whenever the modal re-opens.
  React.useEffect(() => {
    if (open) submittedRef.current = false;
  }, [open]);

  // PropertyLeadForm fires this when a lead is successfully submitted, so the
  // retention popup never bothers a converted visitor.
  React.useEffect(() => {
    const onSuccess = () => { submittedRef.current = true; };
    window.addEventListener('casaothers:lead-submitted', onSuccess);
    return () => window.removeEventListener('casaothers:lead-submitted', onSuccess);
  }, []);

  // Wrapped close: if the user is bailing out (didn't submit), broadcast an
  // "abandoned" event so the retention popup can offer the soft newsletter CTA.
  const handleClose = React.useCallback(() => {
    if (open && !submittedRef.current) {
      window.dispatchEvent(new CustomEvent('casaothers:lead-abandoned'));
    }
    onClose();
  }, [open, onClose]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <div style={styles.backdrop} onClick={handleClose} data-testid="lead-modal-backdrop">
      <div
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-modal"
      >
        <button
          type="button"
          onClick={handleClose}
          style={styles.closeBtn}
          aria-label="Sluiten"
          data-testid="lead-modal-close"
        >
          <X size={20} />
        </button>
        <PropertyLeadForm property={property} defaultCta={defaultCta} />
      </div>
    </div>
  );
};

/** Convenience: combine all three surfaces with shared modal state. */
const PropertyContactCTA = ({ property, showInline = true, showSticky = true }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [defaultCta, setDefaultCta] = useState('info');

  const openModal = (cta) => {
    setDefaultCta(cta || 'info');
    setModalOpen(true);
  };

  return (
    <>
      {showInline && <InlineCTA property={property} onOpen={openModal} />}
      {showSticky && <StickyCTA property={property} onOpen={openModal} />}
      <LeadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        property={property}
        defaultCta={defaultCta}
      />
    </>
  );
};

const styles = {
  inlineIntlNote: {
    display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap',
    margin: '0.6rem 0 0', fontSize: '0.8rem', color: '#C9C7C2', lineHeight: 1.45,
  },
  inlineIntlTag: {
    fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
    background: '#1A1918', color: '#C5A059', padding: '0.12rem 0.35rem',
  },
  inlineIntlEn: { fontStyle: 'italic' },

  // Inline mid-content card
  inlineCard: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1.25rem',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.75rem',
    background: 'linear-gradient(135deg, #1A1918 0%, #2A2826 100%)',
    color: 'white',
    margin: '2rem 0 2.5rem',
    borderLeftWidth: '4px',
    borderLeftStyle: 'solid',
    borderLeftColor: '#C5A059',
  },
  inlineLeft: { flex: '1 1 240px', minWidth: 0 },
  inlineTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.4rem',
    margin: '0 0 0.4rem',
    color: 'white',
    fontWeight: 500,
  },
  inlineText: { margin: 0, opacity: 0.85, fontSize: '0.92rem', lineHeight: 1.5 },
  inlineActions: { display: 'flex', flexWrap: 'wrap', gap: '0.6rem' },
  inlineBtnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.45rem',
    backgroundColor: '#C5A059',
    color: 'white',
    border: 'none',
    padding: '0.85rem 1.5rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
    transition: 'transform 0.15s ease',
  },
  inlineBtnSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.45rem',
    backgroundColor: 'transparent',
    color: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'rgba(255,255,255,0.4)',
    padding: '0.85rem 1.25rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },

  // Sticky bottom bar (mobile-first)
  stickyBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 50,
    display: 'flex',
    gap: '0.5rem',
    padding: '0.65rem 0.75rem calc(0.65rem + env(safe-area-inset-bottom))',
    backgroundColor: 'rgba(255, 255, 255, 0.97)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: '#EAE5DD',
    boxShadow: '0 -4px 12px rgba(0,0,0,0.06)',
  },
  stickyWhatsapp: {
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    backgroundColor: '#25D366',
    color: 'white',
    textDecoration: 'none',
    padding: '0.85rem 1.1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    fontFamily: "'Manrope', sans-serif",
  },
  stickyPrimary: {
    flex: '1 1 auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    backgroundColor: '#C5A059',
    color: 'white',
    border: 'none',
    padding: '0.85rem 1.1rem',
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },

  // Modal overlay
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 100,
    backgroundColor: 'rgba(26, 25, 24, 0.6)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '2rem 1rem',
    overflowY: 'auto',
    backdropFilter: 'blur(4px)',
  },
  modal: {
    position: 'relative',
    width: '100%',
    maxWidth: '480px',
    backgroundColor: 'white',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    animation: 'fadeIn 0.2s ease',
  },
  closeBtn: {
    position: 'absolute',
    top: '0.65rem',
    right: '0.65rem',
    zIndex: 2,
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
    color: '#1A1918',
    cursor: 'pointer',
    borderRadius: '999px',
  },
};

export default PropertyContactCTA;
